/**
 * useChangeStatus — TanStack Query mutation for change status operations.
 *
 * On success: success toast + list refresh + fire onStatusChangeSuccess.
 * On error: error toast.
 *
 * @module hooks/useChangeStatus
 * @see FMPV-MVWC-E003-S005 I-002
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MasterPatternViewEntity } from '../types/service.types';
import type { MasterPatternViewService } from '../types/service.types';
import type { ToastCallback } from './useToast';
import { useToast } from './useToast';
import { useMasterViewStore } from '../store/MasterViewContext';

export interface UseChangeStatusOptions<T extends MasterPatternViewEntity> {
  service: MasterPatternViewService<T>;
  onStatusChangeSuccess?: (record: T) => void;
  toast?: ToastCallback;
  showInternalToasts?: boolean;
  /** Translation function — used for toast messages. Falls back to built-in Spanish strings. */
  t?: (key: string, defaultValue?: string) => string;
}

export function useChangeStatus<T extends MasterPatternViewEntity>({
  service,
  onStatusChangeSuccess,
  toast: toastProp,
  showInternalToasts = true,
  t = (key: string, defaultValue?: string) => defaultValue ?? key,
}: UseChangeStatusOptions<T>) {
  const { showSuccess, showError } = useToast(toastProp, showInternalToasts);
  const closeChangeStatusModal = useMasterViewStore((s) => s.closeChangeStatusModal);
  const clearPendingAction = useMasterViewStore((s) => s.clearPendingAction);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ id, newStatus, companyId }: { id: string; newStatus: boolean; companyId?: string }) =>
      service.changeStatus!(id, newStatus, companyId),
    onSuccess: (record, { id, newStatus }) => {
      showSuccess(t('statusChangeSuccess', 'Estado cambiado exitosamente'));
      // Patch only the affected record in the cache — avoids a full list re-fetch.
      // Works for both pages mode ({ data: T[] }) and infinite mode ({ pages: [...] }).
      queryClient.setQueriesData<unknown>(
        { queryKey: ['masterList'] },
        (oldData: unknown) => {
          if (!oldData) return oldData;
          const patchRow = (r: { id: string }) =>
            r.id === id ? { ...r, isActive: newStatus } : r;
          if (Array.isArray((oldData as { pages?: unknown[] }).pages)) {
            const d = oldData as { pages: { data: { id: string }[] }[] };
            return { ...d, pages: d.pages.map((p) => ({ ...p, data: p.data.map(patchRow) })) };
          }
          const d = oldData as { data: { id: string }[] };
          return { ...d, data: d.data.map(patchRow) };
        },
      );
      closeChangeStatusModal();
      clearPendingAction();
      onStatusChangeSuccess?.(record);
    },
    onError: (error: Error) => {
      showError(error.message || t('statusChangeError', 'Error al cambiar el estado'));
      closeChangeStatusModal();
      clearPendingAction();
    },
  });

  const executeChangeStatus = (id: string, newStatus: boolean, companyId?: string) => {
    mutation.mutate({ id, newStatus, companyId });
  };

  return {
    executeChangeStatus,
    isChanging: mutation.isPending,
  };
}
